import React, { useState } from 'react';
import { Button, Card, Col, Row, Space, Typography } from 'antd';
import FloatingFilter, { SearchDataFilters } from './floating-filter';
import TransactionsForHoursChart from './transactions-for-hour';
import TransactionsGroupByRcChart from './charts/transactions-group-by-response-codes-chart';
import DataMiddleCards from './data-middle-cards';
import MainStatistics from './MainStatistics';
import DistributionForRejectionMessagesChart from './charts/distribution-for-rejection-messages-chart';
import PageWithFilters from 'components/page/page-with-filters';
import useGetRequestUpgrade from '../../hooks/utils/useGetRequestUpgrade';
import dayjs, { Dayjs } from 'dayjs';
import TransactionsGroupByResponseTime from './charts/transactions-group-by-response-time';
import { DownloadOutlined, BarChartOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd/lib';
import { useAppContext } from '../../context/appContext';

const TODAY_DATE = new Date();

const getDateQuery = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
  const startStr = start.format('YYYY-MM-DDT00:00:00');
  const endStr = end.format('YYYY-MM-DDT23:59:59');
  return `start_date=${startStr}&end_date=${endStr}`;
};

const Dashboard = () => {
  const { settings: { issuer } } = useAppContext();
  const [startDate, setStartDate] = useState<Dayjs>(dayjs(TODAY_DATE));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs(TODAY_DATE));
  const dateQuery = getDateQuery(startDate, endDate);

  const { getData: topMerchants, getLoading: loadingTopMerchants, executeGet: refreshTopMerchants } = useGetRequestUpgrade({ path : `v1/reports/top-merchants?${dateQuery}` });
  const { getData: transactionsData, getLoading: loadingTransactionsData, executeGet: refreshTransactionsData } = useGetRequestUpgrade({ path : `v1/reports/transactions?${dateQuery}` });
  const { getData: platformData, getLoading: loadingPlatformData , executeGet: refreshGetPlatformData} = useGetRequestUpgrade({ path : `v1/reports/platform?${dateQuery}` });
  const { getData: transactionsHourData, getLoading: loadingTransactionsHourData, executeGet: refreshTransactionsHours } = useGetRequestUpgrade({ path : `v1/reports/transactions/hours?${dateQuery}` });
  const [loading, setLoading] = useState<boolean>(false);

  const handleSearch = (data: SearchDataFilters) => {
    setStartDate(data.dateRanges[0]);
    setEndDate(data.dateRanges[1]);

    setLoading(true);
    setTimeout( () => {
      setLoading(false);
    }, 2000);
  }

  const handleRefresh = () => {
    setLoading(true);

    refreshGetPlatformData();
    refreshTransactionsHours();
    refreshTransactionsData();
    refreshTopMerchants();

    setTimeout( () => {
      setLoading(false);
    }, 2000);
  }

  const handleDownload = (chartName: string) => {
    const canvas = document.querySelector(`#${chartName} canvas`) as HTMLCanvasElement;
    if (canvas) {
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `${chartName?.concat("-" + endDate?.format('DD-MM-YYYY'))}.png`;
      link.click();
    }
  };

  // @ts-ignore
  return (
    <PageWithFilters title="Métricas">
      <FloatingFilter
        onSubmit={handleSearch}
        onRefresh={handleRefresh}
      />

      <MainStatistics
        loading={loading}
        transactionsData = {transactionsData}
        loadingTransactionsData = {loadingTransactionsData}
        topMerchantsData = {topMerchants}
        loadingTopMerchants = {loadingTopMerchants}
        platformData = {platformData}
        loadingPlatformData = {loadingPlatformData}
      />

      {
        issuer?.is_esor ? (
          <Row gutter={24} style={{ marginTop: '2rem' }}>
            <Col span={16}>
              <Card
                loading={loading}
                title={
                  !loading ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>
                        Agrupados por tiempo de respuesta
                      </Typography.Text>
                      <Tooltip title="Descargar">
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={ () => handleDownload("transactions-group-by-response-time") }
                        />
                      </Tooltip>
                    </div>
                  ) : ''
                }
                bordered={false}
                style={{ minHeight: '100%' }}
              >
                <TransactionsGroupByResponseTime
                  responseTimes = { transactionsData?.response_times }
                />
              </Card>
            </Col>
            <DataMiddleCards
              isEsor={ issuer?.is_esor }
              loading={loading}
              platformData = {platformData}
            />
          </Row>
        ) : (
          <DataMiddleCards
            loading={loading}
            platformData = {platformData}
          />
        )
      }

      <Row gutter={24} style={{ marginTop: '2rem' }}>
        <Col span={12}>
          <Card
            loading={loading}
            title={
              !loading ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>
                    Agrupados por código de respuesta
                  </Typography.Text>
                  <Tooltip title="Descargar">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={ () => handleDownload("transactions-group-by-response-codes") }
                    />
                  </Tooltip>
                </div>
              ) : ''
            }
            style={{
              minHeight: '550px',
              paddingBottom: '10px !important',
              borderRadius: '16px'
            }}>
            {
              transactionsData?.response_codes_details ? (
                <TransactionsGroupByRcChart
                  responseCodes = { transactionsData?.response_codes_details }
                />
              ) : (
                <div style={{
                  height: 300,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  color: '#999'
                }}>
                  <BarChartOutlined
                    style={{ fontSize: '10em' }}
                  />
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>No hay datos para mostrar</Typography.Text>
                </div>
              )
            }
          </Card>
        </Col>
        <Col span={12}>
          <Card
            loading={loading}
            title={
              !loading ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>
                    Distribución por Mensaje de Rechazo
                  </Typography.Text>
                  <Tooltip title="Descargar">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={ () => handleDownload("distribution-for-rejection-messages") }
                    />
                  </Tooltip>
                </div>
              ) : ''
            }
            style={{
              minHeight: '550px',
              paddingBottom: '10px !important',
              borderRadius: '16px'
            }}>
            {
              transactionsData?.rejected?.detail ? (
                <DistributionForRejectionMessagesChart
                  rejecteds = { transactionsData?.rejected?.detail }
                />
              ) : (
                <div style={{
                  height: 300,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  color: '#999'
                }}>
                  <BarChartOutlined
                    style={{ fontSize: '10em' }}
                  />
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>No hay datos para mostrar</Typography.Text>
                </div>
              )
            }
          </Card>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginTop: '2rem' }}>
        <Col span={24}>
          <Card
            loading={loading}
            title={
              !loading ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>
                    Transacciones por hora
                  </Typography.Text>
                  <Tooltip title="Descargar">
                    <Button
                      type="link"
                      icon={<DownloadOutlined />}
                      onClick={ () => handleDownload("transactions-for-hours") }
                    />
                  </Tooltip>
                </div>
              ) : ''
            }
            bordered={false}
            style={{ minHeight: '100%' }}
          >
            {
              transactionsHourData ? (
              <TransactionsForHoursChart transactionsHourData={transactionsHourData} />
              ) : (
                <div style={{
                  height: 300,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  color: '#999'
                }}>
                  <BarChartOutlined
                    style={{ fontSize: '10em' }}
                  />
                  <Typography.Text style={{ fontSize: 16, fontWeight: 500 }}>No hay datos para mostrar</Typography.Text>
                </div>
              )
            }
          </Card>
        </Col>
      </Row>

    </PageWithFilters>
  );
};

export default Dashboard;